// ── Types ─────────────────────────────────────────────────────────────────────

export interface PlantPlacement {
  id: string;
  plantId: string;
  plantName?: string;
  variety?: string;
  plantingLogId?: string;
  plantedDate?: string;
  established?: boolean;
  x: number; // center position in inches from left edge of bed
  y: number; // center position in inches from top edge of bed
}

export interface GardenBed {
  id: string;
  name: string;
  type: 'raised-bed' | 'in-ground' | 'container' | 'custom';
  width: number;  // feet
  height: number; // feet
  plants: PlantPlacement[];
  createdAt: string;
}

export interface GardenPlan {
  id: string;
  name: string;
  beds: GardenBed[];
  createdAt: string;
  updatedAt: string;
}

// ── Internal helpers ───────────────────────────────────────────────────────────

function nanoid(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

const STORAGE_KEY = 'garden-plans';

// ── CRUD ──────────────────────────────────────────────────────────────────────

export function getPlans(): GardenPlan[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as GardenPlan[]) : [];
  } catch {
    return [];
  }
}

export function getPlan(id: string): GardenPlan | null {
  return getPlans().find(p => p.id === id) ?? null;
}

export function savePlan(plan: GardenPlan): void {
  if (typeof window === 'undefined') return;
  try {
    const plans = getPlans();
    const idx = plans.findIndex(p => p.id === plan.id);
    if (idx >= 0) plans[idx] = plan; else plans.push(plan);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(plans));
  } catch { /* quota exceeded */ }
}

export function deletePlan(id: string): void {
  if (typeof window === 'undefined') return;
  try {
    const plans = getPlans().filter(p => p.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(plans));
  } catch {}
}

export function createDefaultPlan(): GardenPlan {
  const now = new Date().toISOString();
  return {
    id: nanoid(),
    name: 'My Garden Plan',
    beds: [{
      id: nanoid(),
      name: 'My First Bed',
      type: 'raised-bed',
      width: 8,
      height: 4,
      plants: [],
      createdAt: now,
    }],
    createdAt: now,
    updatedAt: now,
  };
}

export function createBed(name: string, width: number, height: number, type: GardenBed['type']): GardenBed {
  return {
    id: nanoid(),
    name,
    type,
    width,
    height,
    plants: [],
    createdAt: new Date().toISOString(),
  };
}

export function createPlacement(
  plantId: string,
  x: number,
  y: number,
  metadata: Partial<Omit<PlantPlacement, 'id' | 'plantId' | 'x' | 'y'>> = {},
): PlantPlacement {
  return {
    id: nanoid(),
    plantId,
    ...metadata,
    x,
    y,
  };
}
